import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { code: "", type: "percent", value: 10, min_order: 0, expires_at: "", usage_limit: "" };

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const refresh = () => api.get("/admin/coupons").then((r) => setRows(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value),
        min_order: Number(form.min_order || 0),
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created");
      setOpen(false);
      setForm(empty);
      refresh();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">Promotions</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">Coupons</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="admin-new-coupon" className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]">
          <Plus size={14} /> New coupon
        </button>
      </div>

      <div className="mt-8 bg-white rounded-2xl shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Code</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Type</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Value</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Min Order</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Used / Limit</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.code} className="border-t border-black/5">
                <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                <td className="px-4 py-3 font-mono uppercase">{c.type}</td>
                <td className="px-4 py-3 font-mono">{c.type === "percent" ? `${c.value}%` : `${c.value} JOD`}</td>
                <td className="px-4 py-3 font-mono">{c.min_order || 0} JOD</td>
                <td className="px-4 py-3 font-mono">{c.used || 0} / {c.usage_limit || "∞"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(c.code)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-destructive hover:text-white">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 font-mono text-sm">No coupons yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h2 className="font-display font-extrabold text-2xl">New coupon</h2>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Code</label>
                <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30">
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed (JOD)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest">Value</label>
                  <input required type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest">Min order (JOD)</label>
                  <input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest">Usage limit</label>
                  <input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">Expires (optional)</label>
                <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
              {error && <div className="text-sm text-destructive font-mono">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 h-11 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]">Save</button>
                <button type="button" onClick={() => setOpen(false)} className="h-11 px-5 rounded-full border border-ink text-xs font-mono uppercase tracking-[0.2em]">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
